import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Ask about your records...'
}) => {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      gap: 1,
      alignItems: 'flex-end',
      p: 2,
      borderTop: '1px solid rgba(0,0,0,0.1)',
      backgroundColor: '#ffffff',
    }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        maxRows={4}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={disabled ? 'Waiting for response...' : placeholder}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
            backgroundColor: '#f5f7fa',
          },
          '& .MuiInputBase-input': {
            py: 1.25,
          },
          mb: 0,
        }}
      />
      <IconButton
        onClick={handleSend}
        disabled={disabled || !input.trim()}
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          width: 40,
          height: 40,
          '&:hover': { backgroundColor: 'primary.dark' },
          '&.Mui-disabled': {
            backgroundColor: '#e0e0e0 !important',
            color: '#999 !important',
          },
        }}
      >
        {disabled ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <SendIcon sx={{ fontSize: 20 }} />}
      </IconButton>
    </Box>
  );
};

export default ChatInput;
